import React, { useState, useCallback, useEffect } from 'react';
import { compressImageToJpeg } from '@/lib/compress-image';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, Plus, Trash2, CheckCircle2, User as UserIcon, Camera, RotateCw, ZoomIn, ZoomOut, Save, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useLedgerStore } from '../hooks/use-ledger-store';
import { useLoading } from '../hooks/use-loading';
import { useToast } from '../hooks/use-toast';
import { api } from '../services/api';
import Cropper from 'react-easy-crop';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import WebAuthnManagement from './WebAuthnManagement';

const getCroppedImg = async (imageSrc: string, pixelCrop: any, rotation = 0): Promise<string> => {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => (image.onload = resolve));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  const safeArea = Math.max(image.width, image.height) * 2;
  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(image, safeArea / 2 - image.width / 2, safeArea / 2 - image.height / 2);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - pixelCrop.y)
  );

  return canvas.toDataURL('image/jpeg');
};

export default function AccountManagement({ store }: { store: any }) {
  const { accounts, refresh, currentUser, logout, updateCurrentUser } = store;
  const { showLoading, hideLoading } = useLoading();
  const { showToast } = useToast();
  
  // Profile State
  const [nickname, setNickname] = useState(currentUser?.nickname || '');
  const [avatarUrl, setAvatarUrl] = useState(currentUser?.avatarUrl || '');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  useEffect(() => {
    setNickname(currentUser?.nickname || '');
    setAvatarUrl(currentUser?.avatarUrl || '');
  }, [currentUser]);
  
  // Cropper State
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Bank Info State
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    showLoading('正在壓縮圖片...');
    try {
      const compressed = await compressImageToJpeg(file, 1_048_576);
      const reader = new FileReader();
      reader.onload = () => setImage(reader.result as string);
      reader.readAsDataURL(compressed);
    } finally {
      hideLoading();
    }
  };

  const handleSaveAvatar = async () => {
    if (!image || !croppedAreaPixels) return;
    showLoading('正在套用裁切...');
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels, rotation);
      setAvatarUrl(croppedImage);
      setImage(null);
    } catch (e) {
      console.error(e);
    } finally {
      hideLoading();
    }
  };

  const handleUpdateProfile = async () => {
    setIsUpdatingProfile(true);
    try {
      const formData = new FormData();
      formData.append('nickname', nickname);
      if (avatarUrl.startsWith('data:')) {
        // Already compressed before cropping — just convert dataURL to blob and upload
        const response = await fetch(avatarUrl);
        const blob = await response.blob();
        const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
        formData.append('avatar', file);
      } else {
        formData.append('avatarUrl', avatarUrl);
      }
      const updatedUser = await api.users.updateProfile(formData);
      if (updatedUser) {
        updateCurrentUser(updatedUser);
        refresh();
        showToast('個人資料已更新', 'success');
      }
    } catch (err) {
      console.error(err);
      showToast('更新失敗', 'error');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.accounts.create({ name, accountNumber, isDefault: accounts.length === 0 });
      setName('');
      setAccountNumber('');
      setIsAdding(false);
      refresh();
    } catch (error) {
       console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除此帳號嗎？')) return;
    try {
      await api.accounts.delete(id);
      refresh();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="space-y-12">
      {/* Profile Section */}
      <section className="space-y-8">
        <div>
          <h2 className="text-3xl font-black tracking-tight text-foreground">個人帳戶設定</h2>
          <p className="text-muted-foreground mt-1">更新您的顯示名稱與頭像圖示</p>
        </div>

        <Card className="p-6 md:p-10 bg-muted/20 border-border/50 rounded-[2.5rem] shadow-soft overflow-hidden">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex flex-col items-center gap-6">
              <div className="relative group">
                <Avatar className="h-32 w-32 md:h-48 md:w-48 rounded-[2.5rem] border-4 border-background shadow-xl overflow-hidden ring-4 ring-primary/5 group-hover:ring-primary/20 transition-all">
                  <AvatarImage src={avatarUrl} className="object-cover" />
                  <AvatarFallback className="text-4xl font-black bg-muted">{nickname?.[0] || 'U'}</AvatarFallback>
                </Avatar>
                <label className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white font-bold rounded-[2.5rem] backdrop-blur-[2px]">
                   <Camera size={32} className="mb-2" />
                   <span className="text-xs uppercase tracking-widest">更換頭像</span>
                   <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                </label>
              </div>
              <p className="text-[10px] text-muted-foreground font-black uppercase tracking-[0.2em]">點擊上方頭像可進行編輯</p>
            </div>

            <div className="flex-1 w-full space-y-6">
              <div className="space-y-2">
                <Label className="text-xs font-black text-muted-foreground uppercase tracking-widest ml-1">顯示名稱</Label>
                <div className="relative group/input">
                  <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within/input:text-primary" size={18} />
                  <Input 
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="h-14 pl-12 rounded-2xl bg-background border-none text-lg font-bold shadow-sm focus:ring-2 focus:ring-primary/20"
                    placeholder="輸入暱稱..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-black text-muted-foreground uppercase tracking-widest ml-1">帳號 Email (不可修改)</Label>
                <Input 
                  value={currentUser?.email}
                  disabled
                  className="h-14 rounded-2xl bg-muted/50 border-none font-medium text-muted-foreground cursor-not-allowed"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  onClick={handleUpdateProfile}
                  disabled={isUpdatingProfile}
                  className="flex-1 h-16 rounded-2xl bg-primary text-primary-foreground font-black text-lg shadow-xl hover:shadow-2xl transition-all"
                >
                  {isUpdatingProfile ? <Loader2 className="animate-spin" /> : <Save size={20} className="mr-2" />}
                  更新個人資料
                </Button>
                <Button 
                  variant="outline"
                  onClick={logout}
                  className="h-16 px-8 rounded-2xl font-bold border-destructive/20 text-destructive hover:bg-destructive/10"
                >
                  登出系統
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Bank Accounts Section */}
      <section className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-foreground">銀行收款帳號</h2>
            <p className="text-muted-foreground mt-1">管理您的預設收款銀行資料，方便成員轉帳</p>
          </div>
          <Button onClick={() => setIsAdding(true)} className="rounded-2xl h-14 px-8 flex gap-2 font-black shadow-lg">
            <Plus size={24} />
            <span className="hidden sm:inline">新增帳號</span>
          </Button>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map((account: any) => (
          <motion.div
            key={account.id}
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="p-6 bg-muted/30 border-border/50 rounded-3xl relative group overflow-hidden">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-2xl bg-primary/10 text-primary">
                  <Wallet size={24} />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDelete(account.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-black text-xl">{account.name}</h3>
                  {account.isDefault && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/20 text-secondary text-[10px] font-bold">
                      <CheckCircle2 size={10} />
                      預設
                    </span>
                  )}
                </div>
                <p className="text-2xl font-mono text-primary tracking-wider">{account.accountNumber}</p>
              </div>

              <div className="mt-6 pt-6 border-t border-border/50 flex justify-between items-center">
                <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">持有者: {currentUser?.nickname}</p>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
      </section>

      {/* WebAuthn Credentials Section */}
      <section className="space-y-8">
        <WebAuthnManagement />
      </section>

      {/* Add Bank Account Modal */}
      {isAdding && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-background/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            <Card className="p-8 rounded-[2.5rem] bg-muted shadow-wash border-border font-sans">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-black">新增銀行帳號</h3>
                <Button variant="ghost" size="icon" onClick={() => setIsAdding(false)} className="rounded-full">
                  <X size={20} />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1">銀行名稱</Label>
                  <Input 
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="例如：台新銀行 (812)"
                    className="h-12 rounded-xl bg-background border-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1">帳號號碼</Label>
                  <Input 
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="123456789012"
                    inputMode="numeric"
                    autoComplete="off"
                    className="h-12 rounded-xl bg-background border-none"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <Button variant="ghost" className="flex-1 h-12 rounded-xl" onClick={() => setIsAdding(false)}>取消</Button>
                  <Button className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg" onClick={handleCreate}>儲存帳號</Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Cropper Modal */}
      <AnimatePresence>
        {image && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
               className="absolute inset-0 bg-black/90 backdrop-blur-md" 
             />
             <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                className="relative bg-muted w-full max-w-2xl rounded-[3rem] overflow-hidden shadow-2xl border border-white/5"
             >
                <div className="p-6 md:p-10">
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-2xl font-black uppercase tracking-tighter">編輯頭像</h3>
                    <Button variant="ghost" size="icon" onClick={() => setImage(null)} className="rounded-full">
                       <X size={24} />
                    </Button>
                  </div>

                  <div className="relative h-80 md:h-96 w-full rounded-[2rem] overflow-hidden bg-black shadow-inner">
                    <Cropper
                      image={image}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={1}
                      onCropChange={setCrop}
                      onRotationChange={setRotation}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>

                  <div className="mt-8 space-y-6">
                    <div className="flex flex-col md:flex-row gap-8 items-center">
                       <div className="flex-1 w-full space-y-4">
                         <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                            <span>縮放</span>
                            <span>{Math.round(zoom * 100)}%</span>
                         </div>
                         <div className="flex items-center gap-4">
                           <ZoomOut size={16} className="text-muted-foreground" />
                           <input 
                            type="range" value={zoom} min={1} max={3} step={0.1} 
                            onChange={(e) => setZoom(Number(e.target.value))} 
                            className="flex-1 accent-primary"
                           />
                           <ZoomIn size={16} className="text-muted-foreground" />
                         </div>
                       </div>
                       
                       <div className="flex-1 w-full space-y-4">
                          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                            <span>旋轉角度</span>
                            <span>{rotation}°</span>
                          </div>
                          <div className="flex items-center gap-4">
                             <RotateCw size={16} className="text-muted-foreground" />
                             <input 
                              type="range" value={rotation} min={0} max={360} step={1}
                              onChange={(e) => setRotation(Number(e.target.value))}
                              className="flex-1 accent-primary"
                             />
                          </div>
                       </div>
                    </div>

                    <Button 
                      onClick={handleSaveAvatar}
                      className="w-full h-16 rounded-2xl bg-primary text-primary-foreground font-black text-lg shadow-xl hover:shadow-2xl transition-all"
                    >
                      <CheckCircle2 size={24} className="mr-2" />
                      套用頭像變更
                    </Button>
                  </div>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
